import { PopperPlacementType } from "@mui/base/Popper";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { memo } from "react";
import type { AdvancedTableFilter } from "../advanced-filter";
import { ArrayStringValueDropdown } from "./array-to-string-filter";
import { BooleanValueDropdown } from "./boolean-filter";
import { DateValueDropdown } from "./date-filter";
import { ExistsValueDropdown } from "./exists-filter";

/**
 * Step 2 of the deferred-pill add-filter flow: the value editor for a pending
 * (not-yet-committed) filter, rendered without a pill chip so a value can be
 * chosen before the pill is created. Shared by table2 and the support-v2 toolbar.
 */
export const FilterValueEditor = memo(function FilterValueEditor({
  filter,
  setFilter,
  filters,
  anchor,
  open,
  setOpen,
  placement,
}: {
  filter: AdvancedTableFilter;
  setFilter(filter: AdvancedTableFilter): void;
  filters: AdvancedTableFilter[];
  anchor: HTMLElement | null;
  open: boolean;
  setOpen(open: boolean): void;
  placement?: PopperPlacementType;
}) {
  switch (filter.type) {
    case AdvancedFilterType.ARRAY_TO_STRING:
      return (
        <ArrayStringValueDropdown
          anchor={anchor}
          filter={filter}
          filters={filters}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    case AdvancedFilterType.BOOLEAN:
      return (
        <BooleanValueDropdown
          anchor={anchor}
          filter={filter}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    case AdvancedFilterType.EXISTS:
      return (
        <ExistsValueDropdown
          anchor={anchor}
          filter={filter}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    case AdvancedFilterType.DATE:
      return (
        <DateValueDropdown
          anchor={anchor}
          filter={filter}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    default:
      return null;
  }
});

/**
 * Whether `FilterValueEditor` can render a value editor for the given filter
 * type. Keep this in sync with the switch in `FilterValueEditor` above — these
 * are exactly the cases it renders today. Callers (e.g. table2) use this to
 * decide which fields get the deferred submenu vs. an immediate-commit fallback.
 */
export function isFilterValueEditorSupported(
  type: AdvancedFilterType,
): boolean {
  switch (type) {
    case AdvancedFilterType.ARRAY_TO_STRING:
    case AdvancedFilterType.BOOLEAN:
    case AdvancedFilterType.EXISTS:
    case AdvancedFilterType.DATE:
      return true;
    default:
      return false;
  }
}
